/**
 * How a business's public page identifies itself — in a browser tab, a
 * bookmark, a share sheet, a link preview, a Home Screen icon.
 *
 * BUSINESS FIRST, PLATFORM SECOND. A customer who bookmarks Julio's
 * Photography should see "Julio's Photography", with "Book with Assemble"
 * as the trailing, secondary name. Nothing here is configured by the
 * owner: everything is derived from the name they gave the business and
 * the images they chose to publish. The marketing site and the console
 * keep the product's own identity; this file is for TENANT pages only.
 *
 * Shared by the server (the injected <head>, the manifest, the icon
 * endpoint) and both bundles (the page's own title, the initials mark in
 * the header, the share sheet), so the same name and initials appear
 * wherever a customer meets the business.
 */

/** The product's name, as it appears after the business's own. */
export const PRODUCT_NAME = 'Book with Assemble';

/** "Julio's Photography | Book with Assemble" — the tab, the bookmark, og:title. */
export function pageTitle(name: string): string {
  const clean = tidy(name);
  return clean ? `${clean} | ${PRODUCT_NAME}` : PRODUCT_NAME;
}

/** The one-line description a link preview and the share sheet carry. */
export function identityDescription(name: string): string {
  return `Book an appointment with ${tidy(name)}.`;
}

/** The longest name a Home Screen label shows without truncating. */
export const SHORT_NAME_MAX = 12;

/**
 * A manifest `short_name`: the name when it fits, else the leading words
 * that do, else the name cut. The operating system truncates whatever
 * is given; this only chooses WHICH words survive.
 */
export function shortName(name: string): string {
  const clean = tidy(name);
  if (clean.length <= SHORT_NAME_MAX) return clean;
  const words = clean.split(' ');
  let out = '';
  for (const word of words) {
    const next = out ? `${out} ${word}` : word;
    if (next.length > SHORT_NAME_MAX) break;
    out = next;
  }
  return out || clean.slice(0, SHORT_NAME_MAX);
}

/**
 * Legal and corporate suffixes that say nothing about who the business
 * is: "Maria Beauty LLC" is MB, not ML. Matched whole, case-insensitively,
 * after trailing punctuation is removed.
 */
const SUFFIXES = new Set([
  'llc', 'l.l.c', 'inc', 'incorporated', 'ltd', 'limited', 'co', 'corp', 'corporation', 'company',
  'pllc', 'llp', 'lp', 'plc', 'pc', 'pa', 'dba', 'gmbh', 'sa', 'sl', 'srl', 'bv', 'ag', 'pty', 'cia', 'cía',
]);

/**
 * Articles and joiners in the languages the product is offered in. Never
 * the first letter of a business: "The Barber Shop" is BS and "El Salón
 * de María" is SM. If dropping them leaves nothing, they are kept.
 */
const JOINERS = new Set([
  'the', 'a', 'an', 'and', 'of', 'for', 'at', 'by', 'in', 'on', 'to', 'with',
  'el', 'la', 'los', 'las', 'un', 'una', 'de', 'del', 'y', 'e', 'o', 'u', 'con', 'en', 'por', 'para',
  'le', 'les', 'du', 'des', 'et', 'di', 'da', 'il', 'lo', 'gli', 'i',
]);

/** Whitespace collapsed, ends trimmed. */
function tidy(name: string | null | undefined): string {
  return (name ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Letters and digits with their accents removed, so "É" counts as "E":
 * the icon has outlines for A–Z and 0–9 and nothing else, and an accent
 * on an initial reads as the same letter at 16 pixels.
 */
function plainLetters(word: string): string {
  return word.normalize('NFKD').replace(/[̀-ͯ]/g, '');
}

/**
 * The initials that stand for a business where there is no picture.
 *
 *   Julio's Photography  -> JP
 *   Mike's Barber Shop   -> MB   (the first two words, not first and last)
 *   Maria Beauty         -> MB
 *   The Corner Salon LLC -> CS   (the article and the suffix say nothing)
 *   Bloom                -> B    (one word, one letter)
 *   #1 Nails             -> 1N   (a digit is an initial; a symbol is not)
 *
 * Empty when the name holds no letter or digit at all, and the caller
 * falls back to the product's own mark. Letters outside A–Z (after
 * accents are removed) are returned as they are: a page can draw them
 * with the browser's fonts even where the icon renderer cannot.
 */
export function businessInitials(name: string | null | undefined): string {
  const raw = tidy(name)
    .replace(/&/g, ' and ')
    .replace(/[+/|·•,.;:!?()[\]{}"“”„«»]/g, ' ')
    .split(' ')
    .map((w) => w.replace(/^[^\p{L}\p{N}]+/u, '').replace(/[^\p{L}\p{N}'’-]+$/u, ''))
    .filter((w) => /[\p{L}\p{N}]/u.test(w));
  if (raw.length === 0) return '';
  const withoutSuffixes = raw.filter((w, i) => !(i > 0 && SUFFIXES.has(w.toLowerCase().replace(/\.$/, ''))));
  const meaningful = withoutSuffixes.filter((w) => !JOINERS.has(w.toLowerCase()));
  const words = meaningful.length ? meaningful : withoutSuffixes.length ? withoutSuffixes : raw;
  const initials = words.slice(0, 2).map((w) => plainLetters(w).replace(/[^\p{L}\p{N}]/gu, '').charAt(0)).join('');
  return initials.toUpperCase();
}

/** True when every character of the initials is one the icon renderer can draw (A–Z, 0–9). */
export function initialsDrawable(initials: string): boolean {
  return initials.length > 0 && /^[A-Z0-9]{1,2}$/.test(initials);
}

/**
 * A short, stable fingerprint of what an initials icon is drawn from, put
 * in the icon's URL so that a renamed business or a changed accent is a
 * new address (and a fresh cache entry) rather than a stale picture.
 * FNV-1a, 32 bits, as hex: cheap, deterministic, and the same in the
 * Worker and in Node.
 */
export function identityVersion(...parts: string[]): string {
  let h = 0x811c9dc5;
  for (const ch of parts.join('\u001f')) {
    h ^= ch.codePointAt(0)!;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}
